import * as DsIcon from "@equipmentshare/ds-icon";
import React from "react";
import { CSVLink } from "react-csv";

import { Button } from "~/ButtonV2";
import * as context from "~/Context";
import * as Driver from "~/Driver";
import { Summaries, Summary } from "~/Log";
import * as Log from "~/Log";
import { NotApplicable } from "~/NotApplicable";
import { Time } from "~/Time";
import { Duration } from "~/Time/Duration";

import { Vehicle } from "../Table/Vehicle";

type Download = {
  readonly Date: string;
  readonly Driver: string;
  readonly Distance: string;
  readonly Logs: string;
  readonly Vehicle?: string;
  readonly Duration?: string;
  readonly Exemption?: string;
  readonly Issues?: string;
};

export const fromSummary = (
  { summary }: Log.Summary,
  missingDVIRs = false
): Download => {
  return {
    Date: Time.DateTime.toDateOnlyMMMDYYYY(summary),
    Driver:
      Driver.Name.toString(summary.driver.name) ??
      (summary.driver.id
        ? `Driver ${summary.driver.id}`
        : NotApplicable.text()),
    Distance: `${Summary.distanceMiles(summary).miles} mi`,
    Logs: `${context.useBaseURL()}/e-logs${Log.Route.href(summary)}`,
    // Conditional fields - not ideal setup due to logs + missing dvir
    // table coupling, but should do the trick for now
    ...(missingDVIRs
      ? {
          // Only appears in missing DVIR table
          Vehicle:
            summary.shift?.assets
              .map((v) => Vehicle.formatStringForExport(v, true))
              .join(", ") ?? "Unknown",
        }
      : {
          // Only appear in logs table
          Duration: summary.duration <= 0 ? "" : Duration.toEnglish(summary),
          Exemption: !summary.exemption ? "" : `${summary.exemption}`,
          Issues: Issues.toStringList(summary),
        }),
  };
};

export namespace Issues {
  export const toStringList = ({ issues }: Summary.Issues) =>
    !issues
      ? ""
      : [
          issues.notSigned && Log.Summary.Issues.notSigned(),
          issues.violations.break && Log.Summary.Issues.violationBreak(),
          issues.violations.shift && Log.Summary.Issues.violationShift(),
          issues.violations.drive && Log.Summary.Issues.violationDrive(),
          issues.violations.cycle && Log.Summary.Issues.violationCycle(),
        ]
          .filter((issue) => issue)
          .join(",");
}

function DownloadButton(props: {
  readonly disabled?: boolean;
  readonly asText?: boolean;
}) {
  return (
    <Button
      icon={<DsIcon.Icon name="$icon_download_line" size="$icon_size_4" />}
      type={props.asText ? "text" : "default"}
      style={props.asText ? { padding: 0 } : {}}
      disabled={props.disabled}
    >
      &nbsp;Download CSV
    </Button>
  );
}

type DownloadProps = Summaries & {
  readonly driver?: Driver.Driver;
  readonly asText?: boolean;
  readonly missingDVIRs?: boolean;
};

export function Download({
  summaries,
  driver,
  missingDVIRs,
  asText,
}: DownloadProps) {
  return pipe(
    summaries.map((summary) => fromSummary(summary, missingDVIRs)),
    ([first, ...rest]) =>
      !first ? (
        <DownloadButton disabled />
      ) : (
        <CSVLink
          target="_blank"
          style={{ paddingTop: 0, paddingBottom: 0 }}
          headers={Object.keys(first)}
          data={[first, ...rest]}
          filename={`log-summary-export-${Time.DateTime.toISOWithoutTime(
            Time.DateTime.now()
          )}${driver?.name ? `-${Driver.Name.toString(driver.name)}` : ""}.csv`}
        >
          <DownloadButton asText={asText} />
        </CSVLink>
      )
  );
}
